import React, { createContext, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { showSnackbar as showSnackbarAction, hideSnackbar as hideSnackbarAction, clearSnackbars as clearSnackbarsAction } from '@/store/slices/uiSlice'
import Snackbar, { SnackbarType, SnackbarPosition } from './Snackbar'

interface SnackbarContextType {
    showSnackbar: (message: string, options: {
        type: SnackbarType
        position?: SnackbarPosition
        duration?: number
        showCloseButton?: boolean
    }) => void
    showSuccess: (message: string, options?: {
        position?: SnackbarPosition
        duration?: number
        showCloseButton?: boolean
    }) => void
    showError: (message: string, options?: {
        position?: SnackbarPosition
        duration?: number
        showCloseButton?: boolean
    }) => void
    showWarning: (message: string, options?: {
        position?: SnackbarPosition
        duration?: number
        showCloseButton?: boolean
    }) => void
    showInfo: (message: string, options?: {
        position?: SnackbarPosition
        duration?: number
        showCloseButton?: boolean
    }) => void
    hideSnackbar: (id: string) => void
    clearAllSnackbars: () => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

interface SnackbarProviderProps {
    children: React.ReactNode
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const dispatch = useDispatch()
    const snackbars = useSelector((state: RootState) => state.ui.snackbars)

    const showSnackbar = (message: string, options: { type: SnackbarType; position?: SnackbarPosition; duration?: number; showCloseButton?: boolean }) => {
        dispatch(showSnackbarAction({
            message,
            type: options.type,
            position: options.position,
            duration: options.duration,
            showCloseButton: options.showCloseButton
        }))
    }

    const hideSnackbar = (id: string) => {
        dispatch(hideSnackbarAction(id))
    }

    const clearAllSnackbars = () => {
        dispatch(clearSnackbarsAction())
    }

    const showSuccess = (message: string, options?: { position?: SnackbarPosition; duration?: number; showCloseButton?: boolean }) => {
        dispatch(showSnackbarAction({
            message,
            type: 'success',
            position: options?.position,
            duration: options?.duration,
            showCloseButton: options?.showCloseButton
        }))
    }

    const showError = (message: string, options?: { position?: SnackbarPosition; duration?: number; showCloseButton?: boolean }) => {
        dispatch(showSnackbarAction({
            message,
            type: 'error',
            position: options?.position,
            duration: options?.duration,
            showCloseButton: options?.showCloseButton
        }))
    }

    const showWarning = (message: string, options?: { position?: SnackbarPosition; duration?: number; showCloseButton?: boolean }) => {
        dispatch(showSnackbarAction({
            message,
            type: 'warning',
            position: options?.position,
            duration: options?.duration,
            showCloseButton: options?.showCloseButton
        }))
    }

    const showInfo = (message: string, options?: { position?: SnackbarPosition; duration?: number; showCloseButton?: boolean }) => {
        dispatch(showSnackbarAction({
            message,
            type: 'info',
            position: options?.position,
            duration: options?.duration,
            showCloseButton: options?.showCloseButton
        }))
    }

    const contextValue: SnackbarContextType = {
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideSnackbar,
        clearAllSnackbars
    }

    return (
        <SnackbarContext.Provider value={contextValue}>
            {children}

            {/* Render all active snackbars */}
            {snackbars.map((snackbar) => (
                <Snackbar
                    key={snackbar.id}
                    message={snackbar.message}
                    type={snackbar.type}
                    position={snackbar.position || 'bottom-right'}
                    isVisible={true}
                    onClose={() => hideSnackbar(snackbar.id)}
                    duration={snackbar.duration}
                    showCloseButton={snackbar.showCloseButton}
                />
            ))}
        </SnackbarContext.Provider>
    )
}

export const useSnackbarContext = () => {
    const context = useContext(SnackbarContext)
    if (context === undefined) {
        throw new Error('useSnackbarContext must be used within a SnackbarProvider')
    }
    return context
}